import { toB64 } from '@mgonetwork/bcs';
import { keccak_256 } from '@noble/hashes/sha3';
import { bytesToHex } from '@noble/hashes/utils';

import { bcs } from '../bcs/index.js';
import { MGO_ADDRESS_LENGTH, normalizeMgoAddress } from '../utils/mgo-types.js';
import type { SerializedSignature } from './index.js';
import { IntentScope, messageWithIntent } from './intent.js';

/**
 * Value to be converted into public key.
 */
export type PublicKeyInitData = string | Uint8Array | Iterable<number>;

export function bytesEqual(a: Uint8Array, b: Uint8Array) {
	if (a === b) return true;

	if (a.length !== b.length) {
		return false;
	}

	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}
	return true;
}

/**
 * A public key
 */
export abstract class PublicKey {
	/**
	 * Checks if two public keys are equal
	 */
	equals(publicKey: PublicKey) {
		return bytesEqual(this.toRawBytes(), publicKey.toRawBytes());
	}

	/**
	 * Return the base-64 representation of the public key
	 */
	toBase64() {
		return toB64(this.toRawBytes());
	}

	/**
	 * @deprecated use toBase64 instead.
	 *
	 * Return the base-64 representation of the public key
	 */
	toString() {
		return this.toBase64();
	}

	/**
	 * Return the Mgo representation of the public key encoded in
	 * base-64. A Mgo public key is formed by the concatenation
	 * of the scheme flag with the raw bytes of the public key
	 */
	toMgoPublicKey(): string {
		const bytes = this.toMgoBytes();
		return toB64(bytes);
	}

	verifyWithIntent(
		bytes: Uint8Array,
		signature: Uint8Array | SerializedSignature,
		intent: IntentScope,
	): Promise<boolean> {
		const intentMessage = messageWithIntent(intent, bytes);
		const digest = keccak_256(intentMessage);

		return this.verify(digest, signature);
	}

	/**
	 * Verifies that the signature is valid for for the provided PersonalMessage
	 */
	verifyPersonalMessage(
		message: Uint8Array,
		signature: Uint8Array | SerializedSignature,
	): Promise<boolean> {
		return this.verifyWithIntent(
			bcs.ser(['vector', 'u8'], message).toBytes(),
			signature,
			IntentScope.PersonalMessage,
		);
	}

	/**
	 * Verifies that the signature is valid for for the provided TransactionBlock
	 */
	verifyTransactionBlock(
		transactionBlock: Uint8Array,
		signature: Uint8Array | SerializedSignature,
	): Promise<boolean> {
		return this.verifyWithIntent(transactionBlock, signature, IntentScope.TransactionData);
	}

	/**
	 * Returns the bytes representation of the public key
	 * prefixed with the signature scheme flag
	 */
	toMgoBytes(): Uint8Array {
		const rawBytes = this.toRawBytes();
		const mgoBytes = new Uint8Array(rawBytes.length + 1);
		mgoBytes.set([this.flag()]);
		mgoBytes.set(rawBytes, 1);

		return mgoBytes;
	}

	/**
	 * @deprecated use `toRawBytes` instead.
	 */
	toBytes() {
		return this.toRawBytes();
	}

	/**
	 * Return the Mgo address associated with this Ed25519 public key
	 */
	toMgoAddress(): string {
		// Each hex char represents half a byte, hence hex address doubles the length
		return normalizeMgoAddress(
			bytesToHex(keccak_256(this.toMgoBytes())).slice(0, MGO_ADDRESS_LENGTH * 2),
		);
	}

	/**
	 * Return the byte array representation of the public key
	 */
	abstract toRawBytes(): Uint8Array;

	/**
	 * Return signature scheme flag of the public key
	 */
	abstract flag(): number;

	/**
	 * Verifies that the signature is valid for for the provided message
	 */
	abstract verify(data: Uint8Array, signature: Uint8Array | SerializedSignature): Promise<boolean>;
}
